import { useMemo } from "react";
import useApi from "../useApi";

const BASE = "/users";

/**
 * Paginated user list (GET /api/users/list).
 * Response shape: { success, data: { users: [], pagination: {} } }
 */
export const useUsersList = (params = {}) => {
  const queryParams = useMemo(() => {
    const q = {};
    if (params.page) q.page = params.page;
    if (params.limit) q.limit = params.limit;
    if (params.search) q.search = params.search;
    if (params.status) q.status = params.status;
    if (params.role) q.role = params.role;
    if (params.sortBy) q.sortBy = params.sortBy;
    if (params.sortOrder) q.sortOrder = params.sortOrder;
    return q;
  }, [
    params.page,
    params.limit,
    params.search,
    params.status,
    params.role,
    params.sortBy,
    params.sortOrder,
  ]);

  const api = useApi(`${BASE}/list`, {
    method: "GET",
    params: queryParams,
    immediate: true,
    errorMessage: "Failed to fetch users",
    transformResponse: (res) => res?.data ?? { users: [], pagination: {} },
  });

  const refetch = async () => {
    try {
      const res = await api.execute();
      return { success: true, data: res };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Failed to fetch users",
      };
    }
  };

  return {
    data: api.data?.users ?? [],
    pagination: api.data?.pagination ?? {},
    loading: api.loading,
    error: api.error,
    refetch,
  };
};

/** User statistics (GET /api/users/list/stats). */
export const useUserStats = () => {
  const api = useApi(`${BASE}/list/stats`, {
    method: "GET",
    immediate: true,
    errorMessage: "Failed to fetch user statistics",
    transformResponse: (res) => res?.data ?? null,
  });

  const refetch = async () => {
    try {
      await api.execute();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Failed to fetch user statistics",
      };
    }
  };

  return { data: api.data, loading: api.loading, error: api.error, refetch };
};

/** Single user with role + direct permissions (GET /api/users/:id). */
export const useUserDetail = (id) => {
  const api = useApi(id ? `${BASE}/${id}` : BASE, {
    method: "GET",
    immediate: !!id,
    errorMessage: "Failed to fetch user",
  });

  const refetch = async () => {
    if (!id) return;
    try {
      await api.execute();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Failed to fetch user",
      };
    }
  };

  return { data: api.data, loading: api.loading, error: api.error, refetch };
};

export const useCreateUser = () =>
  useApi(BASE, { method: "POST", errorMessage: "Failed to create user" });

export const useUpdateUser = () => {
  const api = useApi((id) => `${BASE}/${id}`, {
    method: "PUT",
    errorMessage: "Failed to update user",
  });
  const execute = async (id, payload) => {
    if (!id) throw new Error("User id is required");
    return api.execute(id, payload);
  };
  return { execute, loading: api.loading, error: api.error };
};

export const useDeleteUser = () => {
  const api = useApi((id) => `${BASE}/${id}`, {
    method: "DELETE",
    errorMessage: "Failed to delete user",
  });
  const execute = async (id) => {
    if (!id) throw new Error("User id is required");
    return api.execute(id);
  };
  return { execute, loading: api.loading, error: api.error };
};

/** Replace the user's roles (POST /api/users/:id/roles). */
export const useAssignUserRoles = () => {
  const api = useApi((id) => `${BASE}/${id}/roles`, {
    method: "POST",
    errorMessage: "Failed to update roles",
  });
  const execute = async (id, roles) => {
    if (!id) throw new Error("User id is required");
    return api.execute(id, { roles });
  };
  return { execute, loading: api.loading, error: api.error };
};

/** Grant direct permissions (POST /api/users/:id/permissions). */
export const useAssignUserPermissions = () => {
  const api = useApi((id) => `${BASE}/${id}/permissions`, {
    method: "POST",
    errorMessage: "Failed to assign permissions",
  });
  const execute = async (id, permissions) => {
    if (!id) throw new Error("User id is required");
    return api.execute(id, { permissions });
  };
  return { execute, loading: api.loading, error: api.error };
};

/** Remove direct permissions (DELETE /api/users/:id/permissions). */
export const useRemoveUserPermissions = () => {
  const api = useApi((id) => `${BASE}/${id}/permissions`, {
    method: "DELETE",
    errorMessage: "Failed to remove permissions",
  });
  const execute = async (id, permissions) => {
    if (!id) throw new Error("User id is required");
    return api.execute(id, { permissions });
  };
  return { execute, loading: api.loading, error: api.error };
};

/** Resolved (role + direct) permission keys for a user (GET /api/users/:id/permissions). */
export const useUserResolvedPermissions = (id) => {
  const api = useApi(id ? `${BASE}/${id}/permissions` : BASE, {
    method: "GET",
    immediate: !!id,
    errorMessage: "Failed to fetch user permissions",
  });
  return { data: api.data, loading: api.loading, error: api.error, refetch: api.execute };
};
